import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {captureException, captureMessage} from '@sentry/react';
import {useTranslation} from 'react-i18next';
import {PlaidLinkError, PlaidLinkOptions, usePlaidLink} from 'react-plaid-link';
import {useLocation, useNavigate} from 'react-router-dom';

import {
  STORAGE_KYC_ADDRESSES_FORM_FIELDS,
  STORAGE_KYC_FULL_ADDRESS,
} from '@/app/auth/constants/KycConstants';
import {PLAID_UI_EVENTS} from '@/app/auth/events/PlaidUIEvents';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {selectUser, useAppSelector} from '@/app/store';
import {selectTokenData} from '@/app/store/tokenSlice';
import {
  isValidPersistedAddress,
  normalizePersistedAddress,
} from '@/app/utils/KycAddressUtils';
import {LINKS} from '@/constants/LinksConstants';
import {useKycRepository} from '@/dataSource/src/api/kyc/useRepository';
import {AddressFormKyc} from '@/dataSource/src/api/kyc/useRepository';
import {OnboardingVersionV1} from '@/dataSource/src/api/users/constants';
import {TrackingEvent} from '@/services';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {WebViewService} from '@/services/webview/WebViewService';
import logError, {logBreadcrumb} from '@/utils/errors/logError';
import {getFromStorage} from '@/utils/getFromStorage';

import {useTrackers} from './trackers';

type KycPlaidContextType = {
  plaidToken: string;
  isReady: boolean;
  labelButton: string;
  isShowTermsAndConditionsModal: boolean;
  isShowPlaidUiLoading: boolean;
  labelTitle: string;
  isShowPlaidUiLoadingError: boolean;
  openPlaid: () => void;
  openPlaidWhenReady: () => void;
  openTermsAndConditionsModal: () => void;
  closeTermsAndConditionsModal: () => void;
  onPlaidSuccess: () => void;
  onPlaidExit: (error?: string) => void;
  openFreshChat: () => void;
  handleGoToVerificationClick: () => void;
  handleOnClosePrePlaidModal: () => void;
  trackPlaidUIEvent: (eventName: TrackingEvent, error?: string) => void;
};

const ViewModelContext = createContext<KycPlaidContextType | undefined>(
  undefined,
);

export function ViewModelProvider({children}: {children: ReactNode}) {
  //#region [---- [HOOKS] ----]
  const {t} = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  //#endregion

  //#region [---- [STATE] ----]
  const [isShowTermsAndConditionsModal, setIsShowTermsAndConditionsModal] =
    useState<boolean>(false);
  const [isShowPlaidUiLoading, setIsShowPlaidUiLoading] =
    useState<boolean>(true);
  const [isShowPlaidUiLoadingError, setIsShowPlaidUiLoadingError] =
    useState<boolean>(false);
  //#endregion

  //#region [---- [TRACK EVENTS] ----]
  const locationState = (location.state || {}) as {
    formAddress?: AddressFormKyc;
  };
  const persistedAddressFromFields = getFromStorage<AddressFormKyc>(
    STORAGE_KYC_ADDRESSES_FORM_FIELDS,
    localStorage,
  );
  const persistedAddressFromFullAddress = getFromStorage<AddressFormKyc>(
    STORAGE_KYC_FULL_ADDRESS,
    localStorage,
  );
  const persistedFormAddress = normalizePersistedAddress(
    persistedAddressFromFields || persistedAddressFromFullAddress,
  );
  const formAddress = locationState.formAddress || persistedFormAddress;
  const {handleGetTokenPlaid, plaidToken, isGetTokenPlaidError} =
    useKycRepository();
  const userData = useAppSelector(selectUser);
  const authData = useAppSelector(selectTokenData);
  const {trackPlaidUIEvent, trackPlaidEvent} = useTrackers(
    authData?.name,
    userData?.id,
    OnboardingVersionV1,
  );
  //#endregion

  //#region [---- [PLAID CORE LOGIC] ----]
  // Ref to store a pending openPlaid call made before Plaid was ready
  const pendingOpenRef = useRef<boolean>(false);
  // usePlaidLink's `ready` flag stays true across a token swap (it doesn't
  // reset when a fresh token is requested on retry), so it can't be trusted
  // on its own right after we ask for a new token — it may still describe
  // the old, already-exited Plaid handler. isRefreshingPlaidToken bridges
  // that gap until the new token actually lands.
  const [isRefreshingPlaidToken, setIsRefreshingPlaidToken] =
    useState<boolean>(false);
  const retryBaseTokenRef = useRef<string>('');

  const config: PlaidLinkOptions = {
    token: plaidToken,
    onSuccess: (publicToken: string, metadata: any) => {
      handleOnSuccess(publicToken, metadata);
    },
    onExit: (error?: PlaidLinkError) => {
      handleOnExit(error);
    },
    onEvent: (eventName: string, metadata: any) => {
      trackPlaidEvent(PLAID_UI_EVENTS.PLAID_ON_EVENT, {
        eventName,
        metadata,
      });
    },
    onLoad: () => {
      trackPlaidEvent(PLAID_UI_EVENTS.PLAID_ON_LOAD);
    },
  };

  const {open, ready, error: plaidError} = usePlaidLink(config);
  // The one true "safe to open" signal — combines the SDK's ready flag with
  // confirmation that it corresponds to the current (not a stale) token.
  const isPlaidTrulyReady = ready && !isRefreshingPlaidToken;

  const onPlaidSuccess = useCallback(() => {
    navigate('/onboarding/kyc/submitted');
  }, [navigate]);

  const onPlaidExit = useCallback((error?: string) => {
    if (error) {
      logError(error, {
        customTitle: 'KYC Plaid - Error on exit',
        fileName: 'KycPlaid ViewModelContext',
        functionName: 'onPlaidExit',
      });
      captureException(new Error(error), {
        tags: {
          featureName: 'KYC Plaid',
          screenName: 'KycPlaid',
          service: 'Plaid',
        },
        extra: {
          fileName: 'KycPlaid ViewModelContext',
          functionName: 'onPlaidExit',
          error,
        },
      });
    } else {
      logBreadcrumb({
        level: 'info',
        category: 'kyc.plaid',
        type: 'user',
        message: 'Plaid Link Exit',
      });
    }
  }, []);

  const handleOnSuccess = useCallback(
    (publicToken: string, metadata: any) => {
      trackPlaidEvent(PLAID_UI_EVENTS.PLAID_ON_SUCCESS, {
        publicToken,
        metadata,
      });
      logBreadcrumb({
        level: 'info',
        category: 'kyc.plaid',
        type: 'user',
        message: 'Plaid Link Success',
        data: {
          publicToken: publicToken,
          metadata: metadata,
        },
      });
      onPlaidSuccess();
    },
    [onPlaidSuccess, trackPlaidEvent],
  );

  const handleOnExit = useCallback(
    (error?: PlaidLinkError) => {
      trackPlaidEvent(PLAID_UI_EVENTS.PLAID_ON_EXIT, {
        error,
      });
      setIsShowPlaidUiLoading(false);
      // Clean exit (no error) — the same session/token is still usable, so
      // reopening resumes where the user left off (e.g. fields they'd
      // already filled in). An exit WITH error means the Plaid session
      // itself broke — reopening the same handler won't work, so we fall
      // back to the "Try again" + fresh-token flow (isRefreshingPlaidToken).
      setIsShowPlaidUiLoadingError(Boolean(error));
      logBreadcrumb({
        level: error ? 'warning' : 'info',
        category: 'kyc.plaid',
        type: 'info',
        message: 'Plaid Link Exit' + (error ? ' with error' : ' without error'),
        data: {plaidError: error},
      });
      if (error) {
        trackPlaidUIEvent(
          PLAID_UI_EVENTS.SELECT_KYC_PLAID_VERIFICATION_FORM_EXIT_WITH_ERROR,
          error,
          {
            plaidError: error,
            plaidErrorString: JSON.stringify(error),
          },
        );
        captureMessage('Plaid Link Exit', {
          level: 'error',
          extra: {
            fileName: 'KycPlaid ViewModelContext',
            functionName: 'config.onExit',
            error,
            plaidError: JSON.stringify(error),
          },
        });
      } else {
        trackPlaidUIEvent(
          PLAID_UI_EVENTS.SELECT_KYC_PLAID_VERIFICATION_FORM_EXIT,
        );
      }
      onPlaidExit(error?.display_message);
    },
    [onPlaidExit, trackPlaidEvent, trackPlaidUIEvent],
  );

  const openTermsAndConditionsModal = useCallback(() => {
    setIsShowTermsAndConditionsModal(true);
  }, []);

  const closeTermsAndConditionsModal = useCallback(() => {
    setIsShowTermsAndConditionsModal(false);
  }, []);

  const triggerOpen = useCallback(() => {
    trackPlaidUIEvent(PLAID_UI_EVENTS.VIEW_KYC_MODAL_VERIFY_WITH_PLAID);
    open();
    document.body.style.overflow = '';
  }, [open, trackPlaidUIEvent]);

  // Opens Plaid immediately if ready, otherwise queues the open for when it becomes ready
  const openPlaidWhenReady = useCallback(() => {
    logBreadcrumb({
      level: 'info',
      category: 'kyc.plaid',
      type: 'user',
      message: 'Plaid Link Open (queued)',
    });

    if (isPlaidTrulyReady) {
      triggerOpen();
    } else {
      // Queue the open — the useEffect below will fire it once truly ready
      pendingOpenRef.current = true;
    }
  }, [isPlaidTrulyReady, triggerOpen]);

  // Immediate open — shows snackbar error if not ready (used by the main CTA button)
  const openPlaid = useCallback(() => {
    try {
      logBreadcrumb({
        level: 'info',
        category: 'kyc.plaid',
        type: 'user',
        message: 'Plaid Link Open',
      });

      if (isPlaidTrulyReady) {
        triggerOpen();
      } else {
        showSnackbar({
          variant: SnackbarType.ERROR,
          message:
            'Plaid is not ready. Please try again or contact us if the error persists',
        });
        trackPlaidUIEvent(
          PLAID_UI_EVENTS.VIEW_KYC_MODAL_VERIFY_WITH_PLAID_ERROR,
          {error: plaidError},
        );
      }
    } catch (err) {
      captureException(err, {
        tags: {
          featureName: 'KYC Plaid',
          screenName: 'KycPlaid',
          service: 'Plaid',
        },
        extra: {
          fileName: 'KycPlaid ViewModelContext',
          functionName: 'openPlaid',
          error: plaidError,
        },
      });
    }
  }, [isPlaidTrulyReady, triggerOpen, trackPlaidUIEvent, plaidError]);

  const openFreshChat = useCallback(() => {
    if (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({status: WEB_VIEW_MESSAGES.HELP_CENTER}),
      );
      return;
    }
    window.open(LINKS.MODAK_SUPPORT, '_blank');
  }, []);
  //#endregion

  //#region [---- [UI ACTIONS] ----]
  const handleGoToVerificationClick = useCallback(() => {
    if (isShowPlaidUiLoadingError && formAddress) {
      // Snapshot the current token so we know when the retry's fresh one
      // actually lands — see isRefreshingPlaidToken above.
      retryBaseTokenRef.current = plaidToken;
      setIsRefreshingPlaidToken(true);
      handleGetTokenPlaid(formAddress);
      setIsShowPlaidUiLoadingError(false);
      setIsShowPlaidUiLoading(true);
      return;
    }

    trackPlaidUIEvent(PLAID_UI_EVENTS.SELECT_KYC_PLAID_VERIFICATION_FORM);
    openPlaid();
  }, [
    isShowPlaidUiLoadingError,
    formAddress,
    plaidToken,
    trackPlaidUIEvent,
    openPlaid,
    handleGetTokenPlaid,
  ]);

  const handleOnClosePrePlaidModal = useCallback(() => {
    setIsShowPlaidUiLoading(false);
    // Use openPlaidWhenReady instead of openPlaid — queues the call
    // if the SDK isn't ready yet rather than showing an error
    openPlaidWhenReady();
  }, [openPlaidWhenReady]);

  const labelButton = useMemo(() => {
    if (isShowPlaidUiLoadingError) {
      return t('kyc.plaid.retry');
    }

    if (!isPlaidTrulyReady) {
      return t('kyc.plaid.waiting');
    }
    return t('kyc.plaid.go_to_verification_button');
  }, [isPlaidTrulyReady, t, isShowPlaidUiLoadingError]);

  const labelTitle = useMemo(() => {
    return isShowPlaidUiLoading
      ? t('kyc.plaid.loading_title')
      : t('kyc.plaid.title');
  }, [isShowPlaidUiLoading, t]);
  //#endregion

  //#region [---- [EFFECTS] ----]
  useEffect(() => {
    if (isValidPersistedAddress(formAddress)) {
      handleGetTokenPlaid(formAddress);
    } else {
      // formAddress missing — user likely landed here via direct navigation or page refresh
      navigate('/onboarding/kyc', {replace: true});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formAddress]);

  // When Plaid becomes ready, fire any pending open that was requested early
  useEffect(() => {
    if (isPlaidTrulyReady && pendingOpenRef.current) {
      pendingOpenRef.current = false;
      triggerOpen();
    }
  }, [isPlaidTrulyReady, triggerOpen]);

  // Once the retry's fresh token actually replaces the snapshotted one,
  // usePlaidLink's `ready` can be trusted again.
  useEffect(() => {
    if (isRefreshingPlaidToken && plaidToken !== retryBaseTokenRef.current) {
      setIsRefreshingPlaidToken(false);
      logBreadcrumb({
        level: 'info',
        category: 'kyc.plaid',
        type: 'info',
        message: 'Fresh Plaid token landed — ready can be trusted again',
      });
    }
  }, [plaidToken, isRefreshingPlaidToken]);
  //#endregion

  useEffect(() => {
    if (isGetTokenPlaidError) {
      setIsShowPlaidUiLoadingError(true);
      setIsShowPlaidUiLoading(false);
      // The token fetch itself failed (initial load or retry) — stop
      // waiting for a fresh token so isPlaidTrulyReady doesn't stay stuck
      // false forever. The user gets a new snapshot on their next
      // "Try again" tap; if this fired without a retry in flight, this is
      // a no-op since isRefreshingPlaidToken is already false.
      setIsRefreshingPlaidToken(false);
    }
  }, [isGetTokenPlaidError]);
  //#endregion

  //#region [---- [CONTEXT VALUE] ----]
  const contextValue: KycPlaidContextType = {
    plaidToken,
    isReady: isPlaidTrulyReady,
    labelButton,
    isShowTermsAndConditionsModal,
    isShowPlaidUiLoading,
    labelTitle,
    isShowPlaidUiLoadingError,
    openPlaid,
    openPlaidWhenReady,
    openFreshChat,
    openTermsAndConditionsModal,
    closeTermsAndConditionsModal,
    onPlaidSuccess,
    onPlaidExit,
    handleGoToVerificationClick,
    handleOnClosePrePlaidModal,
    trackPlaidUIEvent,
  };
  //#endregion

  return (
    <ViewModelContext.Provider value={contextValue}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('KYC Plaid ViewModelContext is undefined');
  }
  return context;
}
